import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight, Brain, CloudRain, Activity, Repeat, Sparkles, Moon,
  HeartPulse, Baby, Users, Flower2, LifeBuoy, Stethoscope, CalendarClock,
  IndianRupee, ShieldCheck, Languages,
} from "lucide-react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import AppointmentDialog from "@/components/AppointmentDialog";
import useSEO from "@/hooks/useSEO";
import { useLang } from "@/context/LangContext";

const HERO = "https://static.prod-images.emergentagent.com/jobs/e1910145-6c8d-4199-9278-87a3c9131c27/images/e4ec91149491ab48dc4695a28a4823f40ae24262863565b5ce2a0f0e55b3c132.jpeg";

const conditions = [
  {
    icon: CloudRain,
    title: "Depression",
    to: "/conditions/depression",
    signs: "Persistent low mood, loss of interest, fatigue, disturbed sleep or appetite, guilt, poor concentration, thoughts of self-harm.",
    care: "OPD treatment with antidepressants where indicated, cognitive behavioural therapy, and structured follow-up. Most patients improve within 4–8 weeks; severe or treatment-resistant cases can be escalated to our rTMS suite.",
  },
  {
    icon: Activity,
    title: "Bipolar Mood Disorder",
    to: "/conditions/bipolar",
    signs: "Episodes of elevated mood, reduced need for sleep, over-spending or risk-taking, alternating with depressive phases.",
    care: "Long-term mood stabilisers, psychoeducation, mood charting and sleep-routine work. Reviewed every few weeks in OPD to keep you stable for years, not weeks.",
  },
  {
    icon: HeartPulse,
    title: "Anxiety & Panic Disorder",
    to: "/conditions/anxiety",
    signs: "Constant worry, restlessness, palpitations, breathlessness, chest tightness, fear of dying, avoiding travel or crowds.",
    care: "Most panic patients first visit a cardiologist. Once the heart is cleared, SSRIs plus CBT with interoceptive exposure restore normal function — usually within 8–12 weeks of OPD treatment.",
  },
  {
    icon: Repeat,
    title: "Obsessive Compulsive Disorder (OCD)",
    to: "/conditions/ocd",
    signs: "Repeated washing, checking, counting or arranging; intrusive unwanted thoughts; hours lost to rituals.",
    care: "High-dose SSRIs with exposure and response prevention therapy. For resistant OCD we add FDA-approved rTMS protocols — available in Bhavnagar at our own neuromodulation suite.",
  },
  {
    icon: Brain,
    title: "Schizophrenia & Other Psychoses",
    to: "/conditions/schizophrenia",
    signs: "Hearing voices, suspiciousness, delusional beliefs, muttering to self, social withdrawal, self-neglect.",
    care: "Early antipsychotic treatment, long-acting injectables when daily tablets are missed, family psychoeducation and vocational rehabilitation. The earlier treatment starts, the better the long-term outcome.",
  },
  {
    icon: Sparkles,
    title: "Acute Stress & Adjustment Disorders",
    to: "/conditions/stress-adjustment",
    signs: "Overwhelming reaction after a loss, exam failure, job crisis, accident or relationship breakdown; crying spells, hopelessness, inability to function.",
    care: "Short-term, focused OPD support — counselling, sleep restoration and brief medication where needed. Many patients need only a few visits.",
  },
  {
    icon: Moon,
    title: "Insomnia & Sleep Disorders",
    to: "/conditions/insomnia",
    signs: "Difficulty falling or staying asleep, unrefreshing sleep, daytime fatigue, dependence on sleeping pills.",
    care: "We treat the cause, not just the symptom: Cognitive Behavioural Therapy for Insomnia (CBT-I), screening for sleep apnoea, thyroid problems and medication effects, and a plan to taper long-term sedatives safely.",
  },
  {
    icon: Stethoscope,
    title: "Somatoform, Pain & Tension Headache",
    to: "/conditions/somatoform",
    signs: "Repeated physical complaints with normal reports, body aches, burning sensations, tension headache, migraine, giddiness.",
    care: "Genuine symptoms with a neuropsychiatric basis. Treated with appropriate medication, relaxation training and a clear explanation — no more endless investigations.",
  },
  {
    icon: Users,
    title: "Dementia & Geriatric Psychiatry",
    to: "/conditions/dementia",
    signs: "Forgetfulness, repeated questions, getting lost, suspiciousness of family, night-time restlessness, late-life depression.",
    care: "Cognitive assessment, reversible-cause screening, cognitive enhancers where useful, and above all caregiver training — because families carry most of this illness.",
  },
  {
    icon: Baby,
    title: "Child & Adolescent Psychiatry",
    to: "/conditions/adhd-children",
    signs: "ADHD and inattention, autism spectrum concerns, school refusal, falling grades, temper tantrums, bedwetting (enuresis), oppositional behaviour, exam anxiety.",
    care: "Family-centred OPD care working with parents and teachers. Interventions are mostly behavioural; medication is used judiciously and only when truly indicated.",
  },
  {
    icon: Flower2,
    title: "Women's Mental Health",
    to: "/conditions/womens-mental-health",
    signs: "Post-partum depression, severe pre-menstrual mood change (PMDD), menopausal anxiety and sleeplessness, infertility-related distress.",
    care: "Treatment that respects pregnancy and breastfeeding safety, coordinated with your gynaecologist where required, in a private consultation.",
  },
  {
    icon: LifeBuoy,
    title: "Suicide Risk & Crisis Care",
    to: "/conditions/suicide-crisis",
    signs: "Talking about being a burden, giving away belongings, hopelessness, a previous attempt, sudden calm after severe distress.",
    care: "Treat this as an emergency, not a phase. Call +91 94275 60101 — we provide same-day assessment, safety planning, and rapid-acting options including ketamine infusion for active suicidal ideation.",
  },
];

const why = [
  { icon: CalendarClock, title: "Same-week OPD slots", desc: "No referral needed. Walk in, call, or request an appointment online — Mon to Sat, 9 AM to 8 PM." },
  { icon: Stethoscope, title: "MD-qualified psychiatrists", desc: "Consultations by Dr. Kamlesh A. Shah (MD, since 1989) and Dr. Dharmin K. Shah (MD, TMS Physician — AIIMS, New Delhi)." },
  { icon: IndianRupee, title: "Transparent fees", desc: "A standard consultation fee. No packages, no unnecessary tests, no pressure to admit." },
  { icon: ShieldCheck, title: "Strict confidentiality", desc: "Your records are never shared with family, employers or anyone else without your written consent." },
];

const faqs = [
  { q: "Do I need a referral to see a psychiatrist at Manas?", a: "No referral is required. You can walk in during OPD hours (Mon–Sat, 9 AM – 8 PM), call +91 94275 60101, or request an appointment on this website." },
  { q: "Which general psychiatry conditions can be treated in OPD, without admission?", a: "The large majority — depression, anxiety and panic disorder, OCD, insomnia, bipolar disorder in a stable phase, stress and adjustment disorders, ADHD and child behavioural problems, dementia care, post-partum depression and somatoform pain. Admission is reserved for severe psychosis, high suicide risk, or medically supervised detoxification." },
  { q: "Are you the nearest psychiatry hospital for Bhavnagar and Saurashtra?", a: "Manas Neuropsychiatry Hospital is located at Carlton Square, Kalanala, Bhavnagar — 364001, and has served patients from Bhavnagar, Botad, Amreli, Sihor, Palitana, Mahuva, Talaja, Gariadhar, Rajkot and Surat since 1989." },
  { q: "How long does a first psychiatric consultation take?", a: "45–60 minutes, so that a full history and assessment can be done without rushing. Follow-up visits are usually 20–30 minutes." },
  { q: "Will I have to take medication for life?", a: "Usually no. Many conditions — anxiety, depression, insomnia, adjustment disorders — are treated for months, not decades, and then tapered. Some illnesses such as bipolar disorder and schizophrenia do need long-term medication to prevent relapse, and we explain exactly why before you start." },
  { q: "Is psychiatric treatment confidential? Will my employer or family know?", a: "Completely confidential. Nothing is disclosed to family, employers or insurers without your written consent." },
  { q: "Can I bring my elderly parent or my child for a consultation?", a: "Yes. We treat the full age range — child and adolescent psychiatry as well as geriatric psychiatry including dementia — and we involve caregivers in the plan." },
  { q: "Do you accept insurance for psychiatric treatment?", a: "We provide detailed invoices that can be submitted to most insurance providers for reimbursement under outpatient or inpatient psychiatric care." },
  { q: "What if medicines have not worked for my depression before?", a: "That is precisely what our interventional neuromodulation suite is for — rTMS, tDCS and ketamine infusion for treatment-resistant depression and OCD, available in Bhavnagar itself." },
  { q: "Is it an emergency if someone talks about suicide?", a: "Yes. Treat it as a medical emergency and call +91 94275 60101 immediately. We provide same-day assessment and crisis care." },
];

const JSONLD = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "MedicalClinic",
      "@id": "https://manasneuropsychiatryhospital.in/#clinic",
      "name": "Manas Neuropsychiatry Hospital — General Psychiatry OPD",
      "medicalSpecialty": "Psychiatry",
      "url": "https://manasneuropsychiatryhospital.in/general-psychiatry",
      "telephone": "+91-94275-60101",
      "email": "manasneuropsychiatryhospital@gmail.com",
      "foundingDate": "1989",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "2nd floor, Carlton Square, Near Madhavdeep Complex, Kalanala",
        "addressLocality": "Bhavnagar",
        "postalCode": "364001",
        "addressRegion": "Gujarat",
        "addressCountry": "IN",
      },
      "areaServed": ["Bhavnagar", "Botad", "Amreli", "Sihor", "Palitana", "Mahuva", "Talaja", "Saurashtra", "Gujarat"],
      "openingHoursSpecification": [{
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
        "opens": "09:00",
        "closes": "20:00",
      }],
      "availableService": [
        "Depression treatment", "Bipolar mood disorder treatment", "Anxiety and panic disorder treatment",
        "Obsessive compulsive disorder (OCD) treatment", "Schizophrenia and psychosis treatment",
        "Insomnia and sleep disorder treatment", "Dementia and geriatric psychiatry",
        "Child and adolescent psychiatry (ADHD, autism, school refusal)",
        "Women's mental health (post-partum depression, PMDD)",
        "Suicide risk assessment and crisis care", "Somatoform and pain disorders",
      ].map((s) => ({ "@type": "MedicalProcedure", "name": s })),
      "physician": [
        { "@type": "Physician", "name": "Dr. Kamlesh A. Shah", "medicalSpecialty": "Psychiatry" },
        { "@type": "Physician", "name": "Dr. Dharmin K. Shah", "medicalSpecialty": "Psychiatry" },
      ],
    },
    {
      "@type": "FAQPage",
      "mainEntity": faqs.map((f) => ({
        "@type": "Question",
        "name": f.q,
        "acceptedAnswer": { "@type": "Answer", "text": f.a },
      })),
    },
  ],
};

export default function GeneralPsychiatry() {
  useSEO({
    title: "Psychiatrist in Bhavnagar · General Psychiatry OPD — Depression, Anxiety, OCD, Sleep",
    description: "General psychiatry OPD at Manas Neuropsychiatry Hospital, Bhavnagar (since 1989). Treatment for depression, anxiety and panic disorder, OCD, bipolar disorder, schizophrenia, insomnia, ADHD, autism, dementia, post-partum depression and suicidal thoughts. No referral needed — MD psychiatrists Dr. Kamlesh Shah & Dr. Dharmin Shah. Call +91 94275 60101.",
    image: HERO,
    jsonLd: JSONLD,
    lang: "en",
    alternates: [
      { hreflang: "en-IN", href: "https://manasneuropsychiatryhospital.in/general-psychiatry" },
      { hreflang: "gu-IN", href: "https://manasneuropsychiatryhospital.in/gu/general-psychiatry" },
      { hreflang: "x-default", href: "https://manasneuropsychiatryhospital.in/general-psychiatry" },
    ],
  });
  const [open, setOpen] = useState(false);
  const { t } = useLang();

  return (
    <div data-testid="general-psychiatry-page">
      {/* HERO */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 pt-12 md:pt-20 pb-16">
        <p className="eyebrow text-[#A39171] mb-6">{t("General Psychiatry · OPD")}</p>
        <h1 className="font-display text-5xl md:text-7xl text-[#2D2623] leading-[0.95] tracking-tight max-w-5xl">
          {t("Most minds don't need")} <br />
          <span className="italic text-[#E07A5F]">{t("a hospital bed")}</span>. {t("Just a good doctor.")}
        </h1>
        <p className="mt-8 text-xl text-[#5C524E] max-w-3xl leading-relaxed">
          {t("The everyday psychiatry OPD at Manas, Bhavnagar — depression, anxiety and panic, OCD, sleep problems, bipolar disorder, psychosis, childhood behavioural concerns, memory loss in the elderly and women's mental health. Diagnosed properly, treated on an outpatient basis, followed up until you are well.")}
        </p>
        <div className="mt-10 flex flex-col sm:flex-row gap-4">
          <Button data-testid="gp-hero-book" onClick={() => setOpen(true)} className="rounded-full bg-[#2D2623] hover:bg-[#E07A5F] text-[#FDFBF7] px-8 h-14 font-medium">
            {t("Book an OPD Consultation")} <ArrowUpRight className="ml-1 w-4 h-4" />
          </Button>
          <a href="tel:+919427560101" data-testid="gp-hero-call" className="rounded-full border border-[#2D2623]/20 hover:border-[#E07A5F] hover:text-[#E07A5F] text-[#2D2623] px-8 h-14 inline-flex items-center justify-center font-medium transition-colors">
            {t("Call +91 94275 60101")}
          </a>
        </div>
        <p className="mt-6 text-sm text-[#A39171]">
          {t("Mon – Sat · 9:00 AM – 8:00 PM · Kalanala, Bhavnagar · No referral required · Emergency support 24×7")}
        </p>
        <Link
          to="/gu/general-psychiatry"
          data-testid="gp-switch-gujarati"
          className="mt-6 inline-flex items-center gap-2 text-sm px-4 py-2 rounded-full border border-[#A39171]/40 text-[#5C524E] hover:border-[#E07A5F] hover:text-[#E07A5F] transition-colors"
        >
          <Languages className="w-4 h-4" /> ગુજરાતીમાં વાંચો
        </Link>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 mb-24">
        <div className="rounded-3xl overflow-hidden aspect-[16/9] md:aspect-[21/9] relative">
          <img src={HERO} alt="Psychiatrist consulting a patient at Manas Neuropsychiatry Hospital, Bhavnagar" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1A1513]/90 via-[#1A1513]/45 to-transparent" />
          <div className="absolute bottom-8 left-8 right-8 md:left-12 md:right-auto md:max-w-xl">
            <p className="eyebrow text-[#F0CF5B] mb-3 drop-shadow-[0_2px_8px_rgba(0,0,0,0.65)]">{t("Serving Saurashtra since 1989")}</p>
            <p className="font-display text-2xl md:text-3xl leading-tight text-white drop-shadow-[0_2px_10px_rgba(0,0,0,0.7)]">
              {t("Over one lakh consultations across two generations of psychiatrists.")}
            </p>
          </div>
        </div>
      </section>

      {/* CONDITIONS */}
      <section className="pb-24 md:pb-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="max-w-3xl mb-14">
            <p className="eyebrow text-[#A39171] mb-4">{t("Conditions We Treat in OPD")}</p>
            <h2 className="font-display text-4xl md:text-5xl text-[#2D2623] tracking-tight leading-tight">
              {t("Twelve common reasons")} <span className="italic text-[#E07A5F]">{t("people come to us")}</span>.
            </h2>
            <p className="mt-6 text-lg text-[#5C524E] leading-relaxed">
              {t("Nearly every condition below is treated on an outpatient basis — you come, you are assessed, you go home with a plan. Admission is the exception, not the rule.")}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {conditions.map((c, i) => (
              <article key={c.title} data-testid={`gp-condition-${i}`}
                className="bg-[#F4EFE6] rounded-3xl p-8 md:p-9 border border-[#A39171]/15 hover:shadow-[0_20px_50px_rgba(163,145,113,0.14)] transition-shadow">
                <span className="w-12 h-12 rounded-2xl bg-[#2D2623] text-[#FDFBF7] flex items-center justify-center mb-5">
                  <c.icon className="w-5 h-5" />
                </span>
                <h3 className="font-display text-2xl md:text-3xl text-[#2D2623] tracking-tight">{t(c.title)}</h3>
                <p className="mt-4 text-[#5C524E] leading-relaxed">
                  <span className="text-xs uppercase tracking-[0.16em] text-[#A39171] block mb-1.5">{t("What it looks like")}</span>
                  {t(c.signs)}
                </p>
                <p className="mt-4 text-[#2D2623] leading-relaxed">
                  <span className="text-xs uppercase tracking-[0.16em] text-[#A39171] block mb-1.5">{t("How we treat it")}</span>
                  {t(c.care)}
                </p>
                {c.to && (
                  <Link to={c.to} data-testid={`gp-condition-link-${i}`}
                    className="mt-5 inline-flex items-center gap-2 text-[#2D2623] hover:text-[#E07A5F] font-medium text-sm link-underline">
                    {t("Read the full guide")} <ArrowUpRight className="w-3.5 h-3.5" />
                  </Link>
                )}
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="bg-[#F4EFE6]/60 py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <p className="eyebrow text-[#A39171] mb-4">{t("Why patients choose Manas")}</p>
          <h2 className="font-display text-4xl md:text-5xl text-[#2D2623] tracking-tight mb-14 max-w-3xl">
            {t("A psychiatry OPD that runs")} <span className="italic text-[#E07A5F]">{t("on time and on evidence")}</span>.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
            {why.map((w, i) => (
              <div key={w.title} data-testid={`gp-why-${i}`}
                className="bg-[#FDFBF7] rounded-3xl p-7 border border-[#A39171]/15 hover:border-[#E07A5F]/40 transition-all">
                <span className="w-12 h-12 rounded-2xl bg-[#E07A5F] text-white flex items-center justify-center mb-5">
                  <w.icon className="w-5 h-5" />
                </span>
                <h3 className="font-display text-xl text-[#2D2623] leading-tight">{t(w.title)}</h3>
                <p className="text-[#5C524E] mt-2 text-sm leading-relaxed">{t(w.desc)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* VISIT JOURNEY */}
      <section className="py-24 md:py-32">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <p className="eyebrow text-[#A39171] mb-4">{t("What to expect")}</p>
          <h2 className="font-display text-4xl md:text-5xl text-[#2D2623] tracking-tight mb-14">{t("Your OPD visit, step by step.")}</h2>
          <ol className="space-y-10">
            {[
              { t: "Step 1 — Booking", d: "Call, walk in, or request a slot online. No referral letter and no waiting list for routine OPD." },
              { t: "Step 2 — Assessment", d: "A 45–60 minute consultation: history, mental state examination and, where relevant, blood tests or a scan to rule out physical causes." },
              { t: "Step 3 — Diagnosis, explained", d: "You are told what you have, in plain Gujarati or English, along with what it is not. Family is included only if you want them to be." },
              { t: "Step 4 — Treatment plan", d: "Medication where it genuinely helps, therapy where it works better, and lifestyle and sleep correction. Costs and duration are stated upfront." },
              { t: "Step 5 — Follow-up", d: "Reviews every 2–4 weeks initially, then spaced out as you improve. Dose reduction and stopping are planned, not accidental." },
            ].map((s, i) => (
              <li key={s.t} data-testid={`gp-step-${i}`} className="grid grid-cols-12 gap-6 border-b border-[#A39171]/15 pb-10 last:border-none">
                <div className="col-span-12 md:col-span-3">
                  <span className="font-display text-4xl text-[#E07A5F]">0{i + 1}</span>
                </div>
                <div className="col-span-12 md:col-span-9">
                  <h3 className="font-display text-2xl md:text-3xl text-[#2D2623] tracking-tight">{t(s.t)}</h3>
                  <p className="text-[#5C524E] mt-3 text-lg">{t(s.d)}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* WHEN MEDICINES AREN'T ENOUGH — internal links */}
      <section className="pb-24 md:pb-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="bg-[#2D2623] text-[#FDFBF7] rounded-3xl p-8 md:p-14 relative overflow-hidden">
            <div className="absolute inset-0 grain opacity-30 pointer-events-none" />
            <div className="relative grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
              <div className="md:col-span-7">
                <p className="eyebrow text-[#E2C044] mb-4">{t("Beyond the OPD")}</p>
                <h2 className="font-display text-3xl md:text-4xl tracking-tight leading-tight">
                  {t("If tablets alone haven't worked, there is more we can do.")}
                </h2>
                <p className="text-[#FDFBF7]/75 mt-5 leading-relaxed">
                  {t("Manas is the only centre in the region offering rTMS, tDCS and ketamine infusion alongside a routine psychiatry OPD — plus a medically supervised deaddiction programme and a confidential sexology clinic.")}
                </p>
              </div>
              <div className="md:col-span-5 flex flex-col gap-3">
                <Link to="/neuromodulation" data-testid="gp-link-neuromodulation" className="rounded-2xl bg-[#FDFBF7]/5 border border-[#FDFBF7]/15 hover:bg-[#E07A5F] px-6 py-4 flex items-center justify-between transition-colors">
                  <span className="font-medium">{t("Treatment-resistant depression & OCD")}</span>
                  <ArrowUpRight className="w-4 h-4 shrink-0" />
                </Link>
                <Link to="/deaddiction" data-testid="gp-link-deaddiction" className="rounded-2xl bg-[#FDFBF7]/5 border border-[#FDFBF7]/15 hover:bg-[#E07A5F] px-6 py-4 flex items-center justify-between transition-colors">
                  <span className="font-medium">{t("Alcohol, tobacco & drug deaddiction")}</span>
                  <ArrowUpRight className="w-4 h-4 shrink-0" />
                </Link>
                <Link to="/sexology" data-testid="gp-link-sexology" className="rounded-2xl bg-[#FDFBF7]/5 border border-[#FDFBF7]/15 hover:bg-[#E07A5F] px-6 py-4 flex items-center justify-between transition-colors">
                  <span className="font-medium">{t("Sexual health & psychosexual problems")}</span>
                  <ArrowUpRight className="w-4 h-4 shrink-0" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ — feeds FAQPage rich results */}
      <section className="bg-[#F4EFE6]/60 py-24 md:py-32">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <p className="eyebrow text-[#A39171] mb-4">{t("Frequently asked")}</p>
          <h2 className="font-display text-4xl md:text-5xl text-[#2D2623] tracking-tight mb-14 max-w-3xl">
            {t("Practical answers before you visit.")}
          </h2>
          <Accordion type="single" collapsible className="space-y-3">
            {faqs.map((f, i) => (
              <AccordionItem
                key={f.q}
                value={`gp-${i}`}
                data-testid={`gp-faq-${i}`}
                className="bg-[#FDFBF7] rounded-2xl border border-[#A39171]/15 px-6 data-[state=open]:border-[#E07A5F]/35 transition-colors"
              >
                <AccordionTrigger className="text-left font-display text-xl md:text-2xl text-[#2D2623] tracking-tight hover:no-underline py-5">
                  {t(f.q)}
                </AccordionTrigger>
                <AccordionContent className="text-[#5C524E] text-base md:text-lg leading-relaxed pb-6">
                  {t(f.a)}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* AREAS SERVED — local SEO */}
      <section className="py-20 md:py-24">
        <div className="max-w-5xl mx-auto px-6 md:px-10 text-center">
          <p className="eyebrow text-[#A39171] mb-4">{t("Where our patients come from")}</p>
          <h2 className="font-display text-3xl md:text-4xl text-[#2D2623] tracking-tight leading-tight">
            {t("A psychiatry OPD for Bhavnagar and all of Saurashtra.")}
          </h2>
          <p className="mt-6 text-[#5C524E] leading-relaxed max-w-3xl mx-auto">
            {t("Patients travel to Manas Neuropsychiatry Hospital, Kalanala, from Bhavnagar city, Sihor, Palitana, Talaja, Mahuva, Gariadhar, Botad, Amreli, Rajkot, Surat and across Gujarat for psychiatric assessment, deaddiction treatment and neuromodulation.")}
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#2D2623] text-[#FDFBF7] py-20 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 grain opacity-30 pointer-events-none" />
        <div className="max-w-5xl mx-auto px-6 md:px-10 text-center relative">
          <h2 className="font-display text-4xl md:text-5xl tracking-tight leading-tight">
            {t("You don't have to be in crisis")} <span className="italic text-[#E07A5F]">{t("to deserve help")}</span>.
          </h2>
          <p className="text-[#FDFBF7]/70 mt-5 max-w-xl mx-auto">
            {t("Book a routine psychiatry consultation today — Mon to Sat, 9 AM to 8 PM.")}
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
            <Button data-testid="gp-cta-book" onClick={() => setOpen(true)} className="rounded-full bg-[#E07A5F] hover:bg-[#FDFBF7] hover:text-[#2D2623] text-white px-8 h-12 font-medium">{t("Request Appointment")}</Button>
            <a href="tel:+919427560101" className="rounded-full border border-[#FDFBF7]/30 hover:bg-[#FDFBF7] hover:text-[#2D2623] px-8 h-12 inline-flex items-center justify-center font-medium transition-colors">{t("Call")} +91 94275 60101</a>
          </div>
        </div>
      </section>

      <AppointmentDialog open={open} onOpenChange={setOpen} />
    </div>
  );
}
